// frontend/src/components/feedback/OrderFeedbackModal.jsx
// 6.C1 - Post-order feedback modal for platform rating
// Collects user feedback and awards tokens (5-15 based on detail)

import React, { useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import { 
  X, 
  Star, 
  MessageSquare, 
  Coins, 
  CheckCircle, 
  Send,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  Meh
} from 'lucide-react';

// Mutation to submit platform feedback
const SUBMIT_PLATFORM_FEEDBACK = gql`
  mutation SubmitPlatformFeedback($input: PlatformFeedbackInput!) {
    submitPlatformFeedback(input: $input) {
      success
      tokensAwarded
      message
    }
  }
`;

// Feedback categories
const FEEDBACK_CATEGORIES = [
  { id: 'checkout', label: 'Checkout Experience', icon: '🛒' },
  { id: 'delivery', label: 'Delivery/Pickup', icon: '📦' },
  { id: 'products', label: 'Product Quality', icon: '⭐' },
  { id: 'app', label: 'App Usability', icon: '📱' },
  { id: 'support', label: 'Customer Support', icon: '💬' },
  { id: 'value', label: 'Value for Money', icon: '💰' }
];

// Quick feedback options
const QUICK_FEEDBACK = {
  positive: [
    'Fast delivery',
    'Great prices',
    'Easy to use',
    'Good selection',
    'Helpful support',
    'Smooth checkout'
  ],
  negative: [
    'Slow delivery',
    'App issues',
    'Limited selection',
    'Confusing checkout',
    'Missing items',
    'Poor communication'
  ]
};

const OrderFeedbackModal = ({ 
  isOpen, 
  onClose, 
  purchaseId,
  orderTotal,
  initialRating = 0 
}) => {
  const [step, setStep] = useState(1); // 1: rating, 2: details, 3: success
  const [rating, setRating] = useState(initialRating);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [comment, setComment] = useState('');
  const [selectedQuickFeedback, setSelectedQuickFeedback] = useState([]);
  const [tokensEarned, setTokensEarned] = useState(0);

  const [submitFeedback, { loading: submitting }] = useMutation(SUBMIT_PLATFORM_FEEDBACK, {
    onCompleted: (data) => {
      if (data.submitPlatformFeedback.success) {
        setTokensEarned(data.submitPlatformFeedback.tokensAwarded);
        setStep(3);
      }
    },
    onError: (error) => {
      console.error('Feedback submission error:', error);
      // Still show success with minimum tokens on error
      setTokensEarned(5);
      setStep(3);
    }
  });

  const handleRatingSelect = (value) => {
    setRating(value);
    // Auto-advance after rating selection
    setTimeout(() => setStep(2), 300);
  };

  const toggleQuickFeedback = (item) => {
    setSelectedQuickFeedback(prev => 
      prev.includes(item) 
        ? prev.filter(f => f !== item)
        : [...prev, item]
    );
  };

  const handleSubmit = async () => {
    const feedbackText = [
      ...selectedQuickFeedback,
      comment
    ].filter(Boolean).join('. ');

    await submitFeedback({
      variables: {
        input: {
          purchaseId,
          rating,
          category: selectedCategory || 'general',
          comment: feedbackText || undefined
        }
      }
    });
  };

  const calculatePotentialTokens = () => {
    let tokens = 5; // Base
    if (selectedCategory) tokens += 3;
    if (selectedQuickFeedback.length > 0) tokens += 2;
    if (comment.length > 20) tokens += 5;
    return Math.min(tokens, 15);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              {step === 3 ? (
                <CheckCircle size={24} />
              ) : (
                <MessageSquare size={24} />
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {step === 3 ? 'Thank You!' : 'Quick Feedback'}
              </h2>
              <p className="text-purple-100 text-sm">
                {step === 3 
                  ? 'Your feedback helps us improve'
                  : 'Help us improve UniLedge'}
              </p>
            </div>
          </div>

          {/* Token incentive */}
          {step !== 3 && (
            <div className="mt-4 flex items-center gap-2 bg-white/20 rounded-lg px-3 py-2">
              <Coins size={16} className="text-yellow-300" />
              <span className="text-sm">
                Earn <span className="font-bold">{calculatePotentialTokens()} tokens</span> for your feedback!
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {/* Step 1: Rating */}
          {step === 1 && (
            <div className="text-center space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  How was your experience?
                </h3>
                <p className="text-sm text-gray-600">
                  Rate your overall UniLedge experience
                </p>
              </div>

              {/* Star Rating */}
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((value) => (
                  <button
                    key={value}
                    onClick={() => handleRatingSelect(value)}
                    onMouseEnter={() => setHoveredRating(value)}
                    onMouseLeave={() => setHoveredRating(0)}
                    className="p-2 transition-transform hover:scale-110"
                  >
                    <Star 
                      size={40} 
                      className={`transition-colors ${
                        value <= (hoveredRating || rating)
                          ? 'text-yellow-400 fill-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>

              {/* Rating labels */}
              <div className="flex justify-between text-xs text-gray-500 px-2">
                <span>Poor</span>
                <span>Okay</span>
                <span>Good</span>
                <span>Great</span>
                <span>Amazing!</span>
              </div>

              {/* Quick sentiment buttons */}
              <div className="flex justify-center gap-4 pt-4">
                <button
                  onClick={() => handleRatingSelect(1)}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-red-50 transition-colors"
                >
                  <ThumbsDown size={24} className="text-red-400" />
                  <span className="text-xs text-gray-600">Not Great</span>
                </button>
                <button
                  onClick={() => handleRatingSelect(3)}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-yellow-50 transition-colors"
                >
                  <Meh size={24} className="text-yellow-400" />
                  <span className="text-xs text-gray-600">It's Okay</span>
                </button>
                <button
                  onClick={() => handleRatingSelect(5)}
                  className="flex flex-col items-center gap-1 p-3 rounded-lg hover:bg-green-50 transition-colors"
                >
                  <ThumbsUp size={24} className="text-green-400" />
                  <span className="text-xs text-gray-600">Love It!</span>
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Selected rating display */}
              <div className="flex items-center justify-center gap-1 pb-4 border-b">
                {[1, 2, 3, 4, 5].map((value) => (
                  <Star 
                    key={value}
                    size={24} 
                    className={value <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}
                  />
                ))}
                <button 
                  onClick={() => setStep(1)}
                  className="ml-2 text-xs text-blue-600 hover:underline"
                >
                  Change
                </button>
              </div>

              {/* Category selection */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  What area is this feedback about? <span className="text-gray-400">(+3 tokens)</span>
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {FEEDBACK_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        selectedCategory === cat.id
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="mr-2">{cat.icon}</span>
                      <span className="text-sm">{cat.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quick feedback tags */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Quick feedback <span className="text-gray-400">(+2 tokens)</span>
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(rating >= 3 ? QUICK_FEEDBACK.positive : QUICK_FEEDBACK.negative).map((item) => (
                    <button
                      key={item}
                      onClick={() => toggleQuickFeedback(item)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        selectedQuickFeedback.includes(item)
                          ? 'bg-purple-100 text-purple-700 border-purple-300 border'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comment box */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Tell us more <span className="text-gray-400">(+5 tokens for 20+ chars)</span>
                </h4>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="What could we do better? What did you love?"
                  className="w-full p-3 border border-gray-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={3}
                />
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-gray-400">
                    {comment.length} characters
                  </span>
                  {comment.length >= 20 && (
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <Sparkles size={12} />
                      +5 bonus tokens!
                    </span>
                  )}
                </div>
              </div>

              {/* Submit button */}
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit & Earn {calculatePotentialTokens()} Tokens
                  </>
                )}
              </button>

              {/* Skip option */}
              <button
                onClick={() => {
                  // Submit with just rating
                  submitFeedback({
                    variables: {
                      input: {
                        purchaseId,
                        rating,
                        category: 'general'
                      }
                    }
                  });
                }}
                className="w-full text-sm text-gray-500 hover:text-gray-700"
              >
                Skip details (earn 5 tokens)
              </button>
            </div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <div className="text-center space-y-6 py-4">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle size={40} className="text-green-600" />
              </div>

              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Feedback Submitted!
                </h3>
                <p className="text-gray-600">
                  Your voice matters. We use this feedback to make UniLedge better for everyone.
                </p>
              </div>

              {/* Tokens earned celebration */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Coins size={24} className="text-yellow-600" />
                  <span className="text-2xl font-bold text-yellow-700">
                    +{tokensEarned} Tokens
                  </span>
                </div>
                <p className="text-sm text-yellow-700">
                  Added to your wallet!
                </p>
              </div>

              {/* What happens next */}
              <div className="bg-gray-50 rounded-lg p-4 text-left">
                <h4 className="font-medium text-gray-900 mb-2">What happens next?</h4>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    Your feedback is reviewed by our team
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    It impacts our Platform Trust Score
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    We take action on common issues
                  </li>
                </ul>
              </div>

              <button
                onClick={onClose}
                className="w-full bg-gray-900 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderFeedbackModal;